import { observer } from "mobx-react";
import { Dialog } from "@/components-v2/Dialog.tsx";
import { useTranslation } from "@/stores/RootStoreContext.ts";
import { Box, VBox } from "@/components-v2/Box.tsx";
import { MSelect } from "@/components-v2/Select.tsx";
import { MTextInput } from "@/components-v2/TextInput.tsx";
import { MTextArea } from "@/components-v2/TextArea.tsx";
import { Alert } from "@/components-v2/Alert.tsx";
import { LinkButton } from "@/components-v2/Button.tsx";
import { MCheckboxGroup } from "@/components-v2/Checkbox.tsx";
import { OptionFieldModel } from "@/common/commonForms.ts";
import { TMessageKey } from "@/i18n/types.gen.ts";
import { TemplateType } from "@/templates/templateDomain";
import {
  MapTemplateContentState,
  ProjectTemplateContentState,
  SaveAsTemplateDialogState,
  TemplateAttachment,
  TemplateContentState,
  TemplateDetailsState,
  TemplateItem,
} from "@/templates/saveTemplateDomain";

export const SaveAsTemplateDialog = observer(({ state }: { state: SaveAsTemplateDialogState }) => {
  const { t } = useTranslation();
  const accountName = state.details.account.value.name;
  return (
    <Dialog
      title={t(state.title)}
      onCancel={state.onCancel}
      onSubmit={state.onSave}
      submitLabel={t(state.submitLabel)}
      isSubmitting={state.isSaving}
      isSubmitDisabled={!state.content.hasSelection}
    >
      <VBox gap={"lg"}>
        <TemplateDetailsFields details={state.details} />
        <TemplateContentFields content={state.content} accountName={accountName} />
      </VBox>
    </Dialog>
  );
});

const TemplateContentFields = observer(
  ({ content, accountName }: { content: TemplateContentState; accountName: string }) => {
    switch (content.type) {
      case TemplateType.PROJECT:
        return <ProjectTemplateContentFields content={content} accountName={accountName} />;
      case TemplateType.MAP:
        return <MapTemplateContentFields content={content} />;
    }
  },
);

const MapTemplateContentFields = observer(({ content }: { content: MapTemplateContentState }) => {
  const { t } = useTranslation();
  return (
    <>
      <strong>{t("dbot.project.save-as-template.content")}</strong>
      <MCheckboxGroup model={content.selected} />
      {content.error !== undefined && <Alert type={"error"} message={content.error} />}
    </>
  );
});

const ProjectTemplateContentFields = observer(
  ({ content, accountName }: { content: ProjectTemplateContentState; accountName: string }) => {
    const { t } = useTranslation();
    return (
      <>
        <VBox>
          <strong>{t("dbot.project.save-as-template.content")}</strong>
          <div>{t("dbot.project.save-as-template.content-info", { accountName })}</div>
        </VBox>
        <ItemGroup
          label={"dbot.project.save-as-template.decisions"}
          items={content.decisions}
          attachments={content.attachments}
        />
        <ItemGroup
          label={"dbot.project.save-as-template.documents"}
          items={content.documents}
          attachments={content.attachments}
        />
        {content.error !== undefined && <Alert type={"error"} message={content.error} />}
        {content.error === undefined && content.hasSealedSelection && (
          <Alert message={t("dbot.project.save-as-template.info")} type={"info"} icon={"info"} />
        )}
      </>
    );
  },
);

export const TemplateDetailsFields = observer(({ details }: { details: TemplateDetailsState }) => {
  const { t } = useTranslation();
  return (
    <>
      <MSelect
        label={t("dbot.project.save-as-template.account")}
        model={details.account}
        disabled
      />
      <MTextInput label={t("dbot.project.save-as-template.name")} model={details.name} />
      <Description details={details} />
    </>
  );
});

const Description = observer(({ details }: { details: TemplateDetailsState }) => {
  const { t } = useTranslation();
  return details.hasDescription ? (
    <VBox>
      <Box justifyContent={"space-between"}>
        <div>{t("dbot.project.save-as-template.description")}</div>
        <LinkButton label={t("remove")} icon={"trash"} onClick={details.removeDescription} />
      </Box>
      <MTextArea model={details.description} />
    </VBox>
  ) : (
    <Box>
      <LinkButton
        label={t("dbot.project.save-as-template.add-description")}
        onClick={details.addDescription}
        icon={"plus"}
      />
    </Box>
  );
});

const ItemGroup = observer(
  ({
    label,
    items,
    attachments,
  }: {
    label: TMessageKey;
    items: OptionFieldModel<TemplateItem>;
    attachments: OptionFieldModel<TemplateAttachment>;
  }) => {
    const { t } = useTranslation();
    return items.options.length === 0 ? null : (
      <VBox gap={"sm"}>
        <div>{t(label)}</div>
        <MCheckboxGroup
          model={items}
          labelFn={(item) => <>{t(item.label)}</>}
          extraContentFn={(item, checked) => (
            <Attachments item={item} model={attachments} disabled={!checked} />
          )}
        />
      </VBox>
    );
  },
);

const Attachments = observer(
  ({
    item,
    model,
    disabled,
  }: {
    item: TemplateItem;
    model: OptionFieldModel<TemplateAttachment>;
    disabled: boolean;
  }) =>
    item.attachments.length === 0 ? null : (
      <Box padding={"sm"}>
        <MCheckboxGroup
          model={model}
          options={item.attachments}
          disabled={disabled}
          labelFn={(attachment) => <>{attachment.filename}</>}
        />
      </Box>
    ),
);
